import { BookingItem } from 'hooks/useBookingItems';
import { groupBy } from 'lodash';
import type { BookingListItem } from 'types/booking';
import type { ServiceLevel, StatementState } from 'types/enums';
import type { PaymentMethod } from 'types/payment';
import {
  BILLED_OR_PAID_STATEMENT_STATES,
  PAYABLE_STATEMENT_STATES,
} from '../constants/bookings';
import { CLIENT_SERVICES } from '../constants/serviceLevels';
import { sortByTime } from './date';
import moment from './dayjs';
import { isBooker, notBooker } from './roles';
import { isClientServiceLevel } from './users';

// Widened from the `as const` source lists so they can be tested against any
// statement state, not just the two/three literals they contain.
const payableStatementStates: readonly StatementState[] =
  PAYABLE_STATEMENT_STATES;
const billedOrPaidStatementStates: readonly StatementState[] =
  BILLED_OR_PAID_STATEMENT_STATES;

// The flat user shape the booking helpers use, which is deliberately not the
// canonical `User` from types/user.ts — see the note there.
interface User {
  role: string;
  // Null/absent while signed out, which isClientServiceLevel handles.
  clientServiceLevel?: ServiceLevel | null;
  bookableServices: number[];
}

// Only the parts of a booking these helpers need, so they accept both list
// payloads and the single-booking response.
type PayableBooking = Pick<BookingListItem, 'clientService' | 'bookingItems'>;
type DueInvoiceBooking = Pick<BookingListItem, 'clientService'> &
  Partial<Pick<BookingListItem, 'earliestDueInvoiceId'>>;
type AllocatableBooking = Pick<
  BookingListItem,
  'clientService' | 'offlinePayments'
>;

// A booking is payable when:
// - A booking falls within the current month or fell in previous months
// - And an invoice has been raised for the booking
// - And the booking invoice has not yet been fully paid
export const itemIsPayable = (
  item: Pick<
    BookingItem,
    'startTime' | 'statementDueDate' | 'statementState' | 'paymentState'
  >
) =>
  moment(item.startTime).isSameOrBefore(moment().endOf('month')) &&
  !!item.statementDueDate &&
  payableStatementStates.includes(item.statementState) &&
  item.paymentState !== 'paid';

const canPayForBooking = (
  user: Pick<User, 'role' | 'bookableServices'>,
  booking: Pick<BookingListItem, 'clientService'>
) =>
  notBooker(user.role) ||
  (isBooker(user.role) &&
    !!booking.clientService &&
    user.bookableServices.includes(booking.clientService.id));

export const isPayableBooking = (booking: PayableBooking, user: User) =>
  canPayForBooking(user, booking) && booking.bookingItems.some(itemIsPayable);

export const earliestDueInvoice = (
  user: Pick<User, 'role' | 'bookableServices'>,
  booking: DueInvoiceBooking
): number | null => {
  if (canPayForBooking(user, booking) && booking.earliestDueInvoiceId) {
    return booking.earliestDueInvoiceId;
  }

  return null;
};

export const isReadOnlyBooking = (
  booking: Pick<BookingListItem, 'clientService'>,
  user: User
) => {
  if (notBooker(user.role)) {
    return false;
  }

  if (isClientServiceLevel(user.clientServiceLevel)) {
    return false;
  }

  return !canPayForBooking(user, booking);
};

// Recurring bookings repeat the same space/time, so this collapses each
// space-and-hour group down to its earliest occurrence.
export const filterInitialOccuringBookingItems = <
  T extends { startTime: string; endTime: string; space: { id: number } },
>(
  bookingItems: T[]
): T[] => {
  const groupedBookingItems = groupBy(bookingItems, bookingItem => {
    const startHour = moment(bookingItem.startTime).hour();
    const endHour = moment(bookingItem.endTime).hour();

    return `${startHour}${endHour}${bookingItem.space.id}`;
  });

  return Object.keys(groupedBookingItems)
    .map(groupedBookingItemKey => {
      return groupedBookingItems[groupedBookingItemKey].sort(
        sortByTime('startTime')
      )[0];
    })
    .sort(sortByTime('startTime'));
};

export const paymentMethodForBooking = (
  paymentMethods: PaymentMethod[],
  booking: AllocatableBooking
): PaymentMethod | undefined => {
  const candidates = paymentMethods
    .sort(a => (a.serviceLevel !== CLIENT_SERVICES ? -1 : 1))
    .filter(
      paymentMethod =>
        !booking.offlinePayments &&
        (paymentMethod.clientServiceIds.includes(booking.clientService.id) ||
          paymentMethod.serviceLevel === CLIENT_SERVICES)
    );

  const active = candidates.filter(
    paymentMethod => paymentMethod.state === 'active'
  );

  const pending = candidates.filter(
    paymentMethod => paymentMethod.state === 'pending'
  );

  return active.length > 0 ? active[0] : pending[0];
};

export const allBookingsBilledOrPaid = (items: BookingItem[]) =>
  items.every(item =>
    billedOrPaidStatementStates.includes(item.statementState)
  );
